import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, User, LogOut, Menu, X, Clock, TrendingUp, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { supabaseConnectionService } from '../../services/supabaseConnectionService';

const TopNavigation = ({ className = '' }) => {
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [systemVersion, setSystemVersion] = useState('1.0');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [connectionDetails, setConnectionDetails] = useState(null);

  // Enhanced Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([
    'Campaign Analytics Q4',
    'Top QR Performance',
    'User Activity Report'
  ]);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Navigation items
  const navigationItems = [
    {
      label: 'QR Generator',
      path: '/qr-generator',
      icon: 'QrCode',
      tooltip: 'Create and customize QR codes with advanced styling options'
    },
    {
      label: 'QR Import',
      path: '/qr-import-center',
      icon: 'Upload',
      tooltip: 'Import and analyze existing QR codes from various file formats'
    },
    {
      label: 'QR Library',
      path: '/qr-library',
      icon: 'Library',
      tooltip: 'Manage and track all your created QR codes in one place'
    },
    {
      label: 'Analytics Dashboard',
      path: '/dashboard',
      icon: 'BarChart3',
      tooltip: 'Real-time KPI monitoring and system-wide performance metrics'
    },
    {
      label: 'Geographic Performance',
      path: '/geographic-performance',
      icon: 'MapPin',
      tooltip: 'Location-based performance analysis with interactive mapping'
    },
    {
      label: 'Campaign Management',
      path: '/campaign-management',
      icon: 'Target',
      tooltip: 'Multi-campaign tracking and operational control center'
    }
  ];

  // Helper functions
  const isActivePath = (path) => {
    if (location?.pathname === '/' && path === '/qr-generator') {
      return true;
    }
    return location?.pathname === path;
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600 bg-green-50';
      case 'checking': return 'text-blue-600 bg-blue-50';
      case 'disconnected': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'checking': return 'Connecting';
      case 'disconnected': return 'Disconnected';
      default: return 'Unknown Status';
    }
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'CheckCircle';
      case 'checking': return 'Clock';
      case 'disconnected': return 'XCircle';
      default: return 'AlertCircle';
    }
  };

  const handleNavigation = (path) => {
    window.location.href = path;
    setShowMobileMenu(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.log('Sign out error:', error);
    }
  };

  // Helper functions for recent searches management
  const handleDeleteRecentSearch = (indexToDelete, e) => {
    e?.stopPropagation(); // Prevent triggering the search
    setRecentSearches(prev => prev?.filter((_, index) => index !== indexToDelete));
  };

  const handleClearAllRecentSearches = (e) => {
    e?.stopPropagation();
    setRecentSearches([]);
  };

  // Enhanced Search Functions
  const searchCategories = [
    { 
      name: 'QR Codes', 
      icon: 'QrCode', 
      color: 'text-blue-600',
      placeholder: 'Search by name, URL, or campaign...'
    },
    { 
      name: 'Campaigns', 
      icon: 'Target', 
      color: 'text-purple-600',
      placeholder: 'Find campaigns by name or status...'
    },
    { 
      name: 'Analytics', 
      icon: 'TrendingUp', 
      color: 'text-green-600',
      placeholder: 'Search scan data, metrics, reports...'
    },
    { 
      name: 'Users', 
      icon: 'User', 
      color: 'text-orange-600',
      placeholder: 'Find team members or user data...'
    }
  ];

  const quickActions = [
    { name: 'Create New QR Code', action: () => navigate('/qr-generator'), icon: 'Plus' },
    { name: 'View All Campaigns', action: () => navigate('/campaign-management'), icon: 'Target' },
    { name: 'Analytics Dashboard', action: () => navigate('/dashboard'), icon: 'BarChart3' },
    { name: 'Export Report', action: () => console.log('Export'), icon: 'Download' }
  ];

  // Mock search suggestions based on query
  const generateSearchSuggestions = (query) => {
    if (!query || query?.length < 2) return [];
    
    const mockSuggestions = [
      { type: 'qr', title: `QR Code: ${query} Campaign`, subtitle: 'Active • 1.2k scans', icon: 'QrCode' },
      { type: 'campaign', title: `${query} Marketing Campaign`, subtitle: '5 QR codes • Running', icon: 'Target' },
      { type: 'analytics', title: `${query} Performance Report`, subtitle: 'Last 30 days', icon: 'TrendingUp' },
      { type: 'user', title: `User: ${query}`, subtitle: 'Team member', icon: 'User' }
    ];
    
    return mockSuggestions?.filter(item => 
      item?.title?.toLowerCase()?.includes(query?.toLowerCase())
    )?.slice(0, 6);
  };

  const handleSearch = async (query) => {
    if (!query?.trim()) return;
    
    setIsSearching(true);
    
    // Simulate search API call
    setTimeout(() => {
      const suggestions = generateSearchSuggestions(query);
      setSearchResults(suggestions);
      setIsSearching(false);
    }, 300);

    // Add to recent searches if not already present
    if (!recentSearches?.includes(query)) {
      setRecentSearches(prev => [query, ...prev?.slice(0, 4)]);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    
    if (query?.length >= 2) {
      handleSearch(query);
      setShowSearchDropdown(true);
    } else {
      setShowSearchDropdown(query?.length === 0);
      setSearchResults([]);
    }
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      console.log('Searching for:', searchQuery);
      setShowSearchDropdown(false);
      // Implement navigation based on search
    }
  };

  const handleQuickSearch = (query) => {
    setSearchQuery(query);
    handleSearch(query);
    setShowSearchDropdown(true);
    searchInputRef?.current?.focus();
  };

  const handleKeyboardShortcut = (e) => {
    if ((e?.metaKey || e?.ctrlKey) && e?.key === 'k') {
      e?.preventDefault();
      searchInputRef?.current?.focus();
      setShowSearchDropdown(true);
    }
    
    if (e?.key === 'Escape') {
      setShowSearchDropdown(false);
      searchInputRef?.current?.blur();
    }
  };

  // Effects
  useEffect(() => {
    setSystemVersion('1.0');
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, []);

  // Replace the mock connection status effect with real Supabase monitoring
  useEffect(() => {
    const handleConnectionUpdate = (status) => {
      setConnectionStatus(status?.status);
      setConnectionDetails(status?.details);
    };

    // Start monitoring Supabase connection
    const stopMonitoring = supabaseConnectionService?.startMonitoring(
      handleConnectionUpdate,
      30000 // Check every 30 seconds
    );

    // Cleanup on unmount
    return () => {
      stopMonitoring();
    };
  }, []);

  return (
    <>
      {/* Main Header */}
      <header className={`bg-white border-b border-gray-200 shadow-sm ${className}`}>
        <div className="px-4 py-3 lg:px-6">
          <div className="flex items-center justify-between">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="QrCode" size={20} color="white" />
                </div>
                <div className="flex flex-col">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                    <span className="sm:hidden">QR Analytics</span>
                    <span className="hidden sm:inline">QR Analytics Dashboard</span>
                  </h1>
                  <div className={`flex items-center space-x-2 px-2 py-1 rounded ${getConnectionStatusColor()}`}>
                    <Icon 
                      name={getConnectionIcon()} 
                      size={10} 
                      className={connectionStatus === 'connected' ? 'animate-pulse' : ''} 
                    />
                    <span className="text-xs font-medium">{getConnectionStatusText()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems?.map((item) => (
                <Button
                  key={item?.path}
                  variant={isActivePath(item?.path) ? 'default' : 'ghost'}
                  size="sm"
                  className={`
                    px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg
                    ${isActivePath(item?.path) 
                      ? 'bg-blue-50 text-blue-700 border-blue-200' :'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                  onClick={() => handleNavigation(item?.path)}
                  title={item?.tooltip}
                >
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={item?.icon} 
                      size={16} 
                      className={`${isActivePath(item?.path) ? 'text-blue-700' : 'text-gray-500'}`} 
                    />
                    <span className="hidden xl:inline">{item?.label}</span>
                  </div>
                </Button>
              ))}
            </nav>

            {/* Right side controls */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Mobile menu button */}
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              {/* User Profile Dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <span className="hidden lg:block text-sm font-medium text-gray-700 max-w-20 truncate">
                    {userProfile?.full_name || user?.email?.split('@')?.[0] || 'User'}
                  </span>
                </button>
                
                {/* User Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {userProfile?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email}
                    </p>
                    {userProfile?.role && (
                      <p className="text-xs text-blue-600 capitalize">
                        {userProfile?.role}
                      </p>
                    )}
                  </div>
                  
                  <button 
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Search Bar Section */}
        <div className="border-t border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/30 px-4 py-6 lg:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
              {/* Search Input Container */}
              <div className="flex-1 relative" ref={searchRef}>
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onFocus={() => setShowSearchDropdown(true)}
                    placeholder="Search campaigns, QR codes, analytics data..."
                    className="w-full pl-12 pr-20 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm bg-white transition-all duration-200 hover:shadow-md focus:shadow-lg"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                    {isSearching && (
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    )}
                    <div className="flex items-center space-x-1 text-xs text-gray-400">
                      <kbd className="px-2 py-1 bg-gray-100 rounded border">⌘</kbd>
                      <kbd className="px-2 py-1 bg-gray-100 rounded border">K</kbd>
                    </div>
                  </div>
                </form>

                {/* Search Dropdown */}
                {showSearchDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    {searchQuery?.length === 0 ? (
                      // Empty state - show categories and recent searches
                      (<div className="p-4">
                        {/* Search Categories */}
                        <div className="mb-4">
                          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Search Categories</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {searchCategories?.map((category) => (
                              <button
                                key={category?.name}
                                onClick={() => handleQuickSearch(category?.name?.toLowerCase())}
                                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                              >
                                <Icon name={category?.icon} size={18} className={category?.color} />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{category?.name}</p>
                                  <p className="text-xs text-gray-500">{category?.placeholder}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Recent Searches */}
                        {recentSearches?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recent Searches</h3>
                              <button
                                onClick={handleClearAllRecentSearches}
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center space-x-1"
                              >
                                <Trash2 size={12} />
                                <span>Clear all</span>
                              </button>
                            </div>
                            <div className="space-y-1">
                              {recentSearches?.map((search, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between group w-full p-2 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  <button
                                    onClick={() => handleQuickSearch(search)}
                                    className="flex items-center space-x-3 flex-1 text-left"
                                  >
                                    <Clock size={14} className="text-gray-400" />
                                    <span className="text-sm text-gray-700">{search}</span>
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteRecentSearch(index, e)}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all duration-200 ml-2"
                                    title="Delete this search"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>)
                    ) : (
                      // Search results
                      (<div className="p-4">
                        {searchResults?.length > 0 ? (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                              Search Results ({searchResults?.length})
                            </h3>
                            <div className="space-y-1">
                              {searchResults?.map((result, index) => (
                                <button
                                  key={index}
                                  onClick={() => console.log('Navigate to:', result)}
                                  className="flex items-start space-x-3 w-full p-3 rounded-lg hover:bg-blue-50 transition-colors text-left"
                                >
                                  <Icon name={result?.icon} size={18} className="text-blue-600 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{result?.title}</p>
                                    <p className="text-xs text-gray-500">{result?.subtitle}</p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="py-8 text-center">
                            <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No results found for "{searchQuery}"</p>
                            <p className="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
                          </div>
                        )}
                      </div>)
                    )}
                  </div>
                )}
              </div>

              {/* Quick Action Buttons */}
              <div className="flex items-center space-x-2 lg:space-x-3 w-full lg:w-auto">
                <div className="hidden lg:flex items-center space-x-2">
                  {quickActions?.slice(0, 2)
                    ?.filter(action => {
                      // Hide "Create New QR Code" button on QR generator page
                      if (action?.name === 'Create New QR Code' && (location?.pathname === '/qr-generator' || location?.pathname === '/')) {
                        return false;
                      }
                      return true;
                    })
                    ?.map((action, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={action?.action}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400"
                    >
                      <Icon name={action?.icon} size={14} />
                      <span className="text-xs font-medium">{action?.name?.split(' ')?.slice(-2)?.join(' ')}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-gray-200 bg-white shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {navigationItems?.map((item) => (
                <Button
                  key={item?.path}
                  variant={isActivePath(item?.path) ? 'default' : 'ghost'}
                  size="sm"
                  fullWidth
                  className={`
                    justify-start px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg
                    ${isActivePath(item?.path) 
                      ? 'bg-blue-50 text-blue-700 border-blue-200' :'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                  onClick={() => handleNavigation(item?.path)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={item?.icon} 
                      size={16} 
                      className={`${isActivePath(item?.path) ? 'text-blue-700' : 'text-gray-500'}`} 
                    />
                    <span>{item?.label}</span>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        )}
      </header>
      {/* Mobile Bottom Navigation (Fallback) */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="flex items-center justify-around py-2 px-1">
          {navigationItems?.slice(0, 6)?.map((item) => (
            <Button
              key={item?.path}
              variant={isActivePath(item?.path) ? 'default' : 'ghost'}
              size="sm"
              className={`
                flex flex-col items-center justify-center space-y-1 px-1 py-2 min-w-0 rounded-lg flex-1 max-w-16
                ${isActivePath(item?.path) 
                  ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:text-gray-900'
                }
              `}
              onClick={() => handleNavigation(item?.path)}
            >
              <Icon name={item?.icon} size={16} className="flex-shrink-0" />
              <span className="text-xs font-medium truncate leading-tight text-center">
                {item?.path === '/qr-generator' && 'Generator'}
                {item?.path === '/qr-import-center' && 'Import'}
                {item?.path === '/qr-library' && 'Library'}
                {item?.path === '/dashboard' && 'Analytics'}
                {item?.path === '/geographic-performance' && 'Location'}
                {item?.path === '/campaign-management' && 'Campaigns'}
              </span>
            </Button>
          ))}
        </div>
      </nav>
    </>
  );
};

export default TopNavigation;